// The health of the two CloudFormation stacks in the customer's AWS account, told in plain words (2026-10-27).
//   Trust connection: the access role onGuard uses. The customer updates it (TrustStackNotice).
//   Resources: the collector, the data bucket and the reports. onGuard updates it (Update collector, fleet push).
// A code push can fail without the person noticing (the update call answered, the stack then rolled back), so the push
// answer, the stored outcome and the Overview "Needs attention" list all read from here and never disagree.

/** Where the notices send the reader: Settings, AWS deployment tab. */
export const AWS_DEPLOYMENT_TAB = 'settings/aws-deployment';

const WHAT_TO_DO_TRUST = 'The onGuard access role in your AWS account is not allowed to make this change. Update it under Trust connection, then update the collector again.';
const WHAT_TO_DO_TRY_AGAIN = 'Try again, or contact IPC Global support.';

/**
 * The outcome of one click on "Update collector", from the API answer (routes/customer-update-code.mjs). Success is shown
 * only when the answer says success is true; anything else is a notice that says what happened and what to do.
 * @returns {{ tone: 'good'|'info'|'bad', title: string, message: string }}
 */
export function pushResultNotice(res, { trustOutdated = false } = {}) {
  if (res?.action === 'noop') return { tone: 'good', title: 'Already up to date', message: 'The collector already runs the newest version.' };
  if (res?.success === true) return { tone: 'good', title: 'Collector updated', message: 'The collector now runs the newest version.' };
  if (res?.inProgress === true || res?.failureReason === 'in-progress') {
    return {
      tone: 'info',
      title: 'The update is still running',
      message: 'AWS is still applying the update, which can take a few minutes. Check Last code push under Resources in a few minutes.',
    };
  }
  if (res?.failureReason === 'trust-permissions' || trustOutdated) {
    return { tone: 'bad', title: 'The collector was not updated', message: `${WHAT_TO_DO_TRUST} The collector keeps running the previous version.` };
  }
  return {
    tone: 'bad',
    title: 'The collector was not updated',
    message: `The update was rolled back, so the collector keeps running the previous version. ${WHAT_TO_DO_TRY_AGAIN}`,
  };
}

/** The Resources stack in words, from the stored push outcome: { label, tone } for a badge, or null before the first push. */
export function resourcesStatus(customer) {
  const s = customer?.lastCodePushStatus;
  if (!customer?.lastCodePushAt || !s) return null;
  if (s === 'success' || s === 'current') return { label: s, tone: 'good' };
  if (s === 'in-progress') return { label: 'update still running', tone: 'info' };
  return { label: s, tone: 'bad' };
}

/**
 * The Overview "Needs attention" item for a failed code push, or null: { title, body, action }. Told whole (what
 * happened, why it matters, what to do), because the reader may not be the AWS administrator. A push still running is
 * not an attention item, and neither is one that already succeeded.
 */
export function codePushAttention(customer) {
  if (!customer?.stackName || customer.lastCodePushStatus !== 'failed') return null;
  const trust = customer.lastCodePushFailureReason === 'trust-permissions';
  return {
    title: 'The last collector update did not go through',
    body: trust
      ? 'The last update to your onGuard collector was refused because the onGuard access role in your AWS account is not allowed to make the change. '
        + 'The collector keeps running the previous version, so collections continue, but it will not get the newest fixes and checks until it is updated. '
        + 'Update the access role, then update the collector again.'
      : 'The last update to your onGuard collector was rolled back by AWS. '
        + 'The collector keeps running the previous version, so collections continue, but it will not get the newest fixes and checks until an update succeeds. '
        + 'IPC Global can run the update again. Open AWS deployment to see the status, and contact IPC Global support if it stays this way.',
    action: 'Open AWS deployment',
  };
}
